/**
 * Centralized Authentication Service
 * Handles token storage, retrieval, and cleanup with improved security practices
 * 
 * Note: For enhanced security in production, consider:
 * - Using httpOnly cookies set by the backend
 * - Implementing CSRF tokens
 * - Using secure session storage for temporary data
 */

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

// Session storage for tokens (cleared when browser tab closes - more secure than localStorage)
// Falls back to localStorage for persistence if needed
const useSessionStorage = false; // Set to true for enhanced security (tokens cleared on tab close)

const storage = useSessionStorage ? sessionStorage : localStorage;

// Token storage abstraction - can be upgraded to more secure storage later
const tokenStorage = {
  setToken: (token) => {
    if (token) {
      try {
        storage.setItem(TOKEN_KEY, token);
      } catch (e) {
        console.warn('Failed to store token');
      }
    }
  },
  
  getToken: () => {
    try {
      return storage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  },
  
  removeToken: () => {
    try {
      storage.removeItem(TOKEN_KEY);
      // Also clear from the other storage in case of storage switch
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      // Ignore storage errors during cleanup
    }
  }
};

// User data storage
const userStorage = {
  setUser: (user) => {
    if (user) {
      try {
        // Only store non-sensitive user info (no tokens, passwords)
        const safeUser = {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          modules: user.modules
        };
        storage.setItem(USER_KEY, JSON.stringify(safeUser));
      } catch (e) {
        console.warn('Failed to store user data');
      }
    }
  },
  
  getUser: () => {
    try {
      const userData = storage.getItem(USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  },
  
  removeUser: () => {
    try {
      storage.removeItem(USER_KEY);
      localStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(USER_KEY);
    } catch (e) {
      // Ignore storage errors during cleanup
    }
  }
};

/**
 * Store authentication data after successful login
 * @param {string|null} token - Auth token (null to keep existing)
 * @param {object|null} user - User data (null to keep existing)
 */
export const setAuth = (token, user) => {
  if (token !== null) {
    tokenStorage.setToken(token);
  }
  if (user !== null) {
    userStorage.setUser(user);
  }
};

/**
 * Get current authentication token
 */
export const getToken = () => {
  return tokenStorage.getToken();
};

/**
 * Get current user data
 */
export const getUser = () => {
  return userStorage.getUser();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!tokenStorage.getToken();
};

/**
 * Clear all authentication data (logout)
 */
export const clearAuth = () => {
  tokenStorage.removeToken();
  userStorage.removeUser();
};

/**
 * Logout function - clears all auth data
 * Alias for clearAuth for semantic clarity
 */
export const logout = () => {
  clearAuth();
};

/**
 * Get authorization header for API requests
 */
export const getAuthHeader = () => {
  const token = tokenStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default {
  setAuth,
  getToken,
  getUser,
  isAuthenticated,
  clearAuth,
  logout,
  getAuthHeader
};
